import React, { useState, useEffect, FC } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import { ReactComponent as InstallmentsIcon } from "../../../images/interest-free-installments-mobile.svg";
import { isTouchScreen } from "../../../util";
import { convertWithComma } from "../../../util/priceUtil";
import { PAYMENT_MODES } from "../../../constants";
import { useIsRTL } from "../../../hooks";
import { TabbyInfoModal } from "../../../components/tabby/tabbyInfoModal";
import { TamaraInfoModal } from "../../../components/tamara/tamaraInfoModal";
import { useAppSelector } from "../../../redux/store";
import { selectProductDetails } from "../selectors";
import PayLaterOptionItem from "./payLaterOptionItem";
import { PayLaterOption } from "./types";

const Accordion = ({
  className,
  children
}: {
  className?: string;
  children: any;
}) => {
  const { t } = useTranslation("productDetails");
  const [accordionOpened, setAccordionOpened] = useState(false);
  return (
    <div
      className={cn("installments-container", className, {
        "expanded-content": accordionOpened
      })}
    >
      <div
        className="d-flex installments-header"
        onClick={() => setAccordionOpened(!accordionOpened)}
      >
        <InstallmentsIcon className="installments-icon" />
        <h4 className="installments-title">{t("interestFreeInstallments")}</h4>
      </div>
      <div className="installments-content">{children}</div>
    </div>
  );
};

const PayLaterOptions: FC<{
  payLaterOptions: PayLaterOption[];
}> = ({ payLaterOptions }) => {
  const item = useAppSelector(selectProductDetails);
  const [isTouch, setIsTouch] = useState(isTouchScreen());
  const isRTL = useIsRTL();

  useEffect(() => {
    const resizeHandler = () => {
      setIsTouch(isTouchScreen());
    };
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  if (isEmpty(payLaterOptions)) {
    return null;
  }

  const CommonWrapperWeb = (props: { className?: string; children: any }) => (
    <div
      className={cn("d-flex web-wrapper", "mt-4", props.className)}
      children={props.children}
    />
  );

  const CommonWrapperMobile = (props: {
    className?: string;
    children: any;
  }) => (
    <div
      className={cn("installments-container expanded-content", props.className)}
    >
      <div className="installments-content" children={props.children} />
    </div>
  );

  const OptionsWrapper = isTouch ? CommonWrapperMobile : CommonWrapperWeb;

  const normalPrice = convertWithComma(item.price.current || 0);

  const price = 100 * Number(normalPrice);
  const priceForTabbyWidget = Number(normalPrice);

  const optionProps = {
    [PAYMENT_MODES.TABBY]: {
      normalPrice: priceForTabbyWidget,
      ModalComponent: TabbyInfoModal
    },
    [PAYMENT_MODES.TAMARA]: {
      normalPrice,
      ModalComponent: TamaraInfoModal,
      payIconClassName: !isTouch ? (isRTL ? "ml-3" : "mr-3") : ""
    }
  };

  return (
    <OptionsWrapper className="pay_later_options_wrapper">
      {payLaterOptions.map(option => (
        <PayLaterOptionItem
          key={option.paymentType}
          paymentSystemIcon={option.icon}
          minAmountForPayment={option.minAmountForPayment}
          price={price}
          paymentMode={option.paymentType}
          optionMessages={option.messages}
          {...optionProps[option.paymentType]}
        />
      ))}
    </OptionsWrapper>
  );
};

export default React.memo(PayLaterOptions);
